import React from 'react';
import PropTypes from 'prop-types';
import { Fieldset } from '@klarna/bubble-ui';
import { getFieldLabel, addEmptyDropdownItem, centerScrollOnFocus } from '../utils/forms';

export const fieldObjectToArray = (fields = {}) => {
  const fieldsArray = [];
  Object.keys(fields).forEach(key => {
    fieldsArray.push({
      ...fields[key],
      key,
    });
  });
  return fieldsArray;
};

export const prepareExtraProps = ({
  emptyItemOptions: { ...options } = {},
  ...otherExtraProps
} = {}) => {
  const { noEmptyOption, ...finalExtraProps } = otherExtraProps;
  if (otherExtraProps.options && !noEmptyOption) {
    finalExtraProps.options = addEmptyDropdownItem(otherExtraProps.options, options);
  }
  return finalExtraProps;
};

const prepareProps = (field, name, onChange, onBlur, onFocus, isDisabled) => {
  const { type, key, icon, isInitialized, isValid, value, extraProps, ref } = field;
  const error = isInitialized && !isValid;
  const props = {
    label: getFieldLabel(field),
    type,
    name: `${name}-${key}`,
    icon,
    onBlur: () => onBlur(key),
    onChange: val => onChange(key, val),
    onFocus: element => {
      centerScrollOnFocus(element, extraProps);
      return onFocus(key);
    },
    value,
    error,
    disabled: isDisabled,
    ref,
    ...prepareExtraProps(extraProps),
  };
  if (type === 'date') {
    props.onInvalidDate = val => onChange(key, val);
  }
  return props;
};

export const mapObjectToFieldsetArray = ({
  name,
  fields,
  onBlur = key => console.warn(`Missing onBlur function for for ${key}`),
  onChange = key => console.warn(`Missing onChange function for ${key}`),
  onFocus = () => {},
  isDisabled = false,
  tabIndexStart = 1,
}) => {
  const final = fields
    .filter(({ renderInFieldset = true }) => renderInFieldset !== false)
    .sort(({ sortOrder: a = 0 }, { sortOrder: b = 0 }) => a - b)
    .map((field, index) => {
      const { component, size } = field;
      return {
        Component: component,
        size,
        props: {
          ...prepareProps(field, name, onChange, onBlur, onFocus, isDisabled),
          tabIndex: tabIndexStart + index,
        },
      };
    });
  return final;
};

const FieldsetForObject = props => {
  const {
    name,
    fields,
    onBlur,
    onChange,
    onFocus,
    isDisabled,
    tabIndexStart,
    nestedKey = '',
    ...rest
  } = props;

  const fieldsAsArray = fieldObjectToArray(fields);

  const nestedFields = fieldsAsArray.filter(({ fields }) => fields && Object.keys(fields).length);
  const nonNestedFields = fieldsAsArray
    .filter(({ fields }) => !fields || Object.keys(fields).length === 0)
    .filter(({ component }) => !!component);

  return (
    <>
      {nestedFields.length
        ? nestedFields.map(field => (
            <field.component key={field.key}>
              <FieldsetForObject
                name={`${name}.${field.key}`}
                fields={field.fields}
                onBlur={onBlur}
                onChange={onChange}
                onFocus={onFocus}
                isDisabled={isDisabled}
                tabIndexStart={tabIndexStart}
                nestedKey={`${nestedKey}${field.key}.fields.`}
                {...rest}
              />
            </field.component>
          ))
        : null}
      {nonNestedFields.length ? (
        <Fieldset
          hasNoLayoutCalculation
          {...rest}
          fields={mapObjectToFieldsetArray({
            name,
            fields: nonNestedFields,
            onBlur:
              typeof onBlur === 'function'
                ? (key, ...rest) => onBlur(`${nestedKey}${key}`, ...rest)
                : onBlur,
            onChange:
              typeof onChange === 'function'
                ? (key, ...rest) => onChange(`${nestedKey}${key}`, ...rest)
                : onChange,
            onFocus:
              typeof onFocus === 'function'
                ? (key, ...rest) => onFocus(`${nestedKey}${key}`, ...rest)
                : onFocus,
            isDisabled,
            tabIndexStart,
          })}
        />
      ) : null}
    </>
  );
};

FieldsetForObject.propTypes = {
  ...Fieldset.propTypes,
  fields: PropTypes.object.isRequired,
  name: PropTypes.string.isRequired,
  onBlur: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  isDisabled: PropTypes.bool,
  tabIndexStart: PropTypes.number,
  onFocus: PropTypes.func,
};

export default FieldsetForObject;
